import type { Run } from "@/api/runs";
import { formatDuration } from "@/components/runs/run-duration";

/**
 * The run header's meta cluster, derived from the run detail payload ALONE.
 *
 * The mock's cluster is three figures — `3m 27s`, a timestamp, `$1.84`. Only
 * two of the three exist on the wire. `Run` (`api/runs.ts`, extending
 * `ProjectRun` in `api/projects.ts`) carries `run_id`, `flow_name`,
 * `tile_name`, `status`, `submitted_at`, `started_at`, `finished_at`,
 * `submitted_by`, `project_slug` and `invocations[]` — and no cost, price or
 * currency field of any kind. So the header renders no cost figure: this is
 * chrome, and a header is not the place to add a fetch or to print a number
 * the API never sent.
 *
 * That omission is also the safe side of flowstack#1089 finding 1, which is
 * still live in `CostPreviewPanel.tsx:100-108`: there the "estimate" BADGE is
 * gated on the server's `pricing_basis` but the FIGURE is not, so an older
 * server (absent field) yields a bare dollar amount with nothing qualifying
 * it. A figure and its qualification have to appear and disappear together;
 * with no cost field at all here, the only shape that satisfies that is to
 * render neither.
 */

/**
 * The run's COMPUTE time — `finished_at - started_at` — or `null`.
 *
 * **This figure changed meaning under it in flowstack#1270, so read the label
 * before the arithmetic.** Both timestamps used to be hardcoded `None` on the
 * wire, which meant this returned `null` for every run that has ever existed
 * and the header's chip has never once rendered. They now carry the window
 * **VCM's controller recorded for the job's task**, so the chip appears for the
 * first time — and what it shows is compute time, which EXCLUDES every second
 * the run spent queued. The old wording ("wall time it occupied") is no longer
 * true of it: wall time is a larger, different figure the DAG lanes derive from
 * `created_at`/`updated_at`, and `run-elapsed.ts`'s "since submitted" is a
 * third. None of the three is a substitute for another, so the call site names
 * this one out loud rather than calling it "elapsed".
 *
 * WHY THE GUARD IS BOTH-TIMESTAMPS AND NOT A STATUS TEST. It still holds, for
 * the same reason and now for a second one: a `running` run has no end, a
 * `failed_validation` run never started, a cancelled run's end is when the
 * operator intervened — and, since #1270, a run VCM never timed has no window
 * at all whatever its status. `finished - started` means exactly one thing
 * only when both ends exist, so that is the only case with a figure.
 *
 * `null` therefore means **not recorded**, and it is never a fact about the
 * run — not a cache hit, not a fast run, not a failure. That reading is the
 * defect #1270 was filed to stop. Because omitting a chip and showing an empty
 * one are equally silent about WHY, the header pairs this with
 * `run-timing.ts::absentTimingLabel`, which says "not recorded" in words on a
 * run that has settled without one.
 *
 * Consequently there is no live-ticking value for an in-flight run. A clock
 * that advances is behaviour, not chrome, and would need a timer this
 * component does not have.
 *
 * Unparseable or inverted timestamps are `null` too — a header that printed
 * `NaN` or a negative span would be asserting something about the run that
 * the data does not support. (The server clamps its own derived
 * `duration_seconds` at zero instead; this one has no server-side pair to
 * agree with, and a header chip can simply be absent.)
 *
 * The FORMATTING is `run-duration.ts`, the same function the workspace's
 * per-Tile duration column renders through, so the run's compute time reads
 * identically in the header and in the table below it.
 */
export function elapsedLabel(run: Pick<Run, "started_at" | "finished_at">): string | null {
  if (!run.started_at || !run.finished_at) return null;
  const started = Date.parse(run.started_at);
  const finished = Date.parse(run.finished_at);
  if (Number.isNaN(started) || Number.isNaN(finished)) return null;
  const seconds = (finished - started) / 1000;
  if (seconds < 0) return null;
  return formatDuration(seconds);
}

/**
 * The tile-count chip's label, pluralised — or `null` where the rollup cannot
 * support a count and the chip is omitted.
 *
 * `invocations` is the per-Tile rollup the Flow runner emits at compile time —
 * one row per fanned child, exactly one for a single-Tile run — so its length
 * is a real API-provided count and not an inference from the flow name.
 *
 * EXCEPT on a FLOW run whose rollup carries no node-named rows, which is what
 * the `null` is for. `GET /api/runs/{id}` has not finished B2's submission
 * writes: it synthesises a ONE-row rollup describing the run itself, with
 * `node_name: null` (`server/routes/runs.py::get_run`, "B1 ships a one-row
 * invocations list"). For a standalone Tile run that row is honest — the run IS
 * its one tile. For a flow run it is a placeholder, and the chip rendered from
 * it read "1 tile" over a 14-node `full_affdo_sequential` (found while building
 * VIS/V1, which puts a real per-Tile count from the run-scoped flow-dag
 * directly below it, so the two would have contradicted each other on one
 * screen).
 *
 * The narrowing is deliberately the weakest one that closes it: a flow run
 * where EVERY row lacks a `node_name` is the placeholder and nothing else,
 * since the runner stamps `node_name` on every flow child it attaches. So this
 * suppresses only a chip provably describing the API's stand-in — the empty
 * rollup included, because "0 tiles" over a 15-node flow is the same false
 * claim with a different number. Absent beats wrong.
 */
export function tileCountLabel(run: Pick<Run, "invocations" | "flow_name">): string | null {
  if (run.flow_name !== null && run.invocations.every((inv) => inv.node_name === null)) return null;
  const count = run.invocations.length;
  return `${count} ${count === 1 ? "tile" : "tiles"}`;
}
